import { useMemo } from 'react';

import { useProductNutritionData } from '../useProductNutritionData';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates memoized macro nutrition data based on the provided base product,
 * both as an ordered list of valid entries and as a record keyed by nutrient.
 *
 * NOTE: `entries` omits any nutritional metric that is not valid; `record` keeps it as `null`.
 */
export const useProductMicroNutritionData = (params: Params) => {
  const completeNutritionData = useProductNutritionData(params);

  return useMemo<MicroNutritionData>(() => {
    const { proteinG, totalCarbsG, totalFatG, dietaryFiberG, calories } = completeNutritionData;

    const record = { proteinG, totalCarbsG, totalFatG, dietaryFiberG, calories };
    const entries = Object.values(record).filter(checkIfValidEntry);

    return { entries, record };
  }, [completeNutritionData]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfValidEntry(entry: NutritionData): entry is NutritionEntry {
  return Boolean(entry);
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = Parameters<typeof useProductNutritionData>[0];

type NutritionData = ReturnType<typeof useProductNutritionData>[MicroNutrition];

type NutritionEntry = NonNullable<NutritionData>;

type MicroNutrition = 'proteinG' | 'totalCarbsG' | 'totalFatG' | 'dietaryFiberG' | 'calories';

type MicroNutritionData = {
  entries: NutritionEntry[];
  record: Record<MicroNutrition, NutritionData>;
};
